import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Plus, Loader2, ShoppingBag, DollarSign, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 outline-none transition-all";

export default function Marketplace() {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", price: "", category: "", image: "" });

  const load = useCallback(async () => { const { data } = await api.get("/deals"); setDeals(data); setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.price) return toast.error("Title and price required");
    setSaving(true);
    try {
      await api.post("/deals", { ...form, price: parseFloat(form.price) });
      toast.success("Deal listed! 🛍️");
      setOpen(false); setForm({ title: "", description: "", price: "", category: "", image: "" }); await load();
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Marketplace</h1>
          <p className="text-slate-500">Browse sponsored content packages & deals.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-deal-btn" className="flex items-center gap-2 px-5 py-3 rounded-full font-bold text-white brand-gradient shadow-lg shadow-pink-500/30 hover:-translate-y-0.5 transition-all">
              <Plus className="h-5 w-5" /> List a deal
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl">List a sponsored deal</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <input data-testid="deal-title" placeholder="Deal title" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <textarea data-testid="deal-desc" placeholder="What's included…" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="deal-price" type="number" placeholder="Price ($)" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                <input data-testid="deal-category" placeholder="Category" className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <input data-testid="deal-image" placeholder="Image URL (optional)" className={inputCls} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
              <button data-testid="deal-save" onClick={create} disabled={saving} className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-bold text-white brand-gradient disabled:opacity-50">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Publish deal
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>
      ) : deals.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><ShoppingBag className="h-10 w-10 mx-auto mb-3 text-pink-300" /><p className="font-semibold">No deals listed yet.</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {deals.map((d, i) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:-translate-y-1 transition-all duration-200" data-testid="deal-card">
              {d.image ? <img src={d.image} alt={d.title} className="h-44 w-full object-cover" /> : <div className="h-44 brand-gradient" />}
              <div className="p-5">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold uppercase"><Tag className="h-3 w-3" />{d.category || "Deal"}</span>
                <h3 className="font-display font-bold text-lg mt-2 text-slate-900">{d.title}</h3>
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{d.description}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="flex items-center font-extrabold text-xl text-slate-900"><DollarSign className="h-5 w-5 text-green-500" />{d.price.toLocaleString()}</span>
                  <span className="text-xs text-slate-400">{d.seller_name}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
